import { useEffect, useRef } from "react";
import { Dismiss24Regular } from "@fluentui/react-icons";

import { getBackendUri } from "../../api/backendUri";
import { EXAMPLE_UI_COPY, ExampleLanguage, getLocalizedText } from "../Example/ExampleCategories";
import styles from "./GuideOverlay.module.css";

interface GuideOverlayProps {
    exampleLanguage: ExampleLanguage;
    onClose: () => void;
}

// The gateway serves the business guide (gateway/manual); it opens over the assistant's own screen in every channel.
export const GuideOverlay = ({ exampleLanguage, onClose }: GuideOverlayProps) => {
    const closeRef = useRef<HTMLButtonElement>(null);
    const guideUrl = `${getBackendUri()}/manual/`;
    const title = getLocalizedText(EXAMPLE_UI_COPY.guideTitle, exampleLanguage);

    useEffect(() => {
        closeRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
            <div className={styles.header}>
                <span className={styles.title}>{title}</span>
                <a className={styles.newTab} href={guideUrl} target="_blank" rel="noreferrer">
                    {getLocalizedText(EXAMPLE_UI_COPY.openGuideInTab, exampleLanguage)}
                </a>
                <button
                    ref={closeRef}
                    type="button"
                    className={styles.close}
                    onClick={onClose}
                    aria-label={getLocalizedText(EXAMPLE_UI_COPY.closeGuide, exampleLanguage)}
                >
                    <Dismiss24Regular />
                </button>
            </div>
            {/* The gateway is on the private network: Chrome asks a cross-origin frame to be allowed to reach it. */}
            <iframe className={styles.frame} src={guideUrl} title={title} allow="local-network-access" />
        </div>
    );
};
